import { useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, ShieldCheck, Sparkles, CalendarCheck } from 'lucide-react';
import { TID, EVENT_TIERS } from '@yalidavet/shared';
import HeroSearch from '@/components/search/HeroSearch';
import QuickFilters from '@/components/search/QuickFilters';
import MansionCard from '@/components/mansion/MansionCard';
import { BANNERS, FEATURE_DAVET } from '@/lib/images';
import { api } from '@/lib/api';

export default function HomePage() {
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ['featured'],
    queryFn: () => api.featured(8),
  });

  return (
    <>
      {/* ============ BANNER: full-screen editorial hero ============ */}
      <section className="relative isolate flex min-h-[88vh] items-center justify-center overflow-hidden">
        <img
          src={BANNERS.home}
          alt=""
          aria-hidden
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-burgundy-950/60 via-burgundy-900/45 to-burgundy-950/75" aria-hidden />

        <div className="yd-container flex flex-col items-center gap-8 py-24 text-center">
          <h1 className="font-display text-5xl font-light leading-tight tracking-wide text-white sm:text-6xl xl:text-7xl animate-fade-in">
            Bir Masalı Tasarlamak
          </h1>

          <div className="h-px w-24 bg-gold-400/70" aria-hidden />

          <p className="max-w-2xl text-base font-light leading-relaxed tracking-wide text-cream-100 sm:text-lg animate-slide-in">
            Boğaz'ın en seçkin yalı ve köşklerinde düğün, nikah, nişan ve özel davetleriniz için
            misafirperverliğin en zarif hâli.
          </p>

          <a
            href="#kesfet"
            className="mt-6 animate-bounce text-cream-100 transition-colors hover:text-white"
            aria-label="Aşağı kaydır"
          >
            <svg className="h-9 w-9" fill="none" stroke="currentColor" strokeWidth={1.2} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          </a>
        </div>
      </section>

      {/* ============ SEARCH BAR ============ */}
      <section id="kesfet" className="border-b border-cream-200 bg-cream-50">
        <div className="yd-container -mt-10 pb-12">
          <div className="rounded-2xl bg-white p-5 shadow-card ring-1 ring-gold-200/60">
            <HeroSearch />
          </div>
          <div className="mt-6 flex justify-center">
            <QuickFilters
              active={null}
              onSelect={(id) => navigate(id ? `/ara?eventTypeId=${id}` : '/ara')}
            />
          </div>
        </div>
      </section>

      {/* ============ EDITORIAL INTRO ============ */}
      <section className="bg-white">
        <div className="yd-container grid items-center gap-14 py-20 sm:py-28 lg:grid-cols-2 lg:gap-20">
          <div className="text-center lg:text-left">
            <h2 className="font-display text-4xl font-light leading-snug tracking-wide text-burgundy-900 sm:text-5xl">
              Yalıda Davet'te <br className="hidden sm:block" /> Unutulmaz Organizasyonlar
            </h2>
            <div className="mx-auto mt-8 h-px w-24 bg-gold-400/70 lg:mx-0" aria-hidden />
            <div className="mt-10 space-y-6 text-lg font-light leading-relaxed text-yali-700">
              <p>
                Boğaziçi'nin yüzyıllara direnen ahşap sahil yalıları, bir zamanlar paşaların ve prenseslerin
                ağırlandığı salonlarıyla bugün sizin davetinize ev sahipliği yapıyor.
              </p>
              <p>
                Her adımınızın tarihe tanıklık etmiş mekânlara taşıdığı bu zarif yolculuğa bizimle
                çıkmaya ne dersiniz?
              </p>
            </div>
            <Link
              to="/ara"
              className="mt-10 inline-block border-b border-gold-500 pb-1 font-display text-sm uppercase tracking-[0.25em] text-burgundy-800 transition-colors hover:border-burgundy-700 hover:text-burgundy-600"
            >
              Davetinizi planlamak için tıklayınız
            </Link>
          </div>

          <figure className="group relative overflow-hidden rounded-sm shadow-card">
            <img
              src={FEATURE_DAVET}
              alt="Yalı bahçesinde hazırlanmış davet alanı"
              loading="lazy"
              className="h-[26rem] w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105 lg:h-[32rem]"
            />
            <div
              className="pointer-events-none absolute inset-0 ring-1 ring-inset ring-gold-200/40"
              aria-hidden
            />
          </figure>
        </div>
      </section>

      {/* ============ TRUST STRIP ============ */}
      <section className="border-y border-cream-200 bg-white">
        <div className="yd-container grid gap-10 py-16 sm:grid-cols-3">
          {[
            { icon: ShieldCheck, title: 'Doğrulanmış ev sahipleri', body: 'Tapu ve kimlik kontrolünden geçmiş mülkler.' },
            { icon: Sparkles, title: 'Organizasyona hazır', body: 'Sahne, catering ve otopark alanları önceden onaylı.' },
            { icon: CalendarCheck, title: 'Kapasite garantisi', body: 'Misafir sayınıza uygun bahçe metrekaresi otomatik eşleşir.' },
          ].map(({ icon: Icon, title, body }) => (
            <div key={title} className="flex flex-col items-center gap-3 text-center">
              <Icon className="h-8 w-8 text-gold-600" strokeWidth={1.2} aria-hidden />
              <h2 className="font-display text-lg font-light tracking-wide text-burgundy-900">{title}</h2>
              <p className="text-sm font-light leading-relaxed text-yali-600">{body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============ POPÜLER MEKANLAR ============ */}
      <section className="yd-container py-20 sm:py-28" data-testid={TID.popularVenues}>
        <div className="mb-14 text-center">
          <h2 className="font-display text-4xl font-light tracking-wide text-burgundy-900 sm:text-5xl">Popüler Mekânlar</h2>
          <div className="mx-auto mt-6 h-px w-24 bg-gold-400/70" aria-hidden />
          <p className="mt-6 text-base font-light text-yali-600">En çok tercih edilen yalı ve köşkler</p>
        </div>

        {isLoading ? (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="yd-skeleton h-80 rounded-xl2" />
            ))}
          </div>
        ) : (
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {data?.items.map((m) => <MansionCard key={m.id} mansion={m} />)}
          </div>
        )}

        <div className="mt-14 text-center">
          <Link
            to="/ara"
            className="inline-block border-b border-gold-500 pb-1 font-display text-sm uppercase tracking-[0.25em] text-burgundy-800 transition-colors hover:border-burgundy-700 hover:text-burgundy-600"
            data-testid={TID.popularVenuesAll}
          >
            Tüm seçenekler için tıklayınız
          </Link>
        </div>
      </section>

      {/* ============ CAPACITY RULES EXPLAINER ============ */}
      <section className="mx-3 bg-cream-100 sm:mx-8 xl:mx-16">
        <div className="yd-container py-20 sm:py-28">
          <div className="text-center">
            <h2 className="font-display text-4xl font-light tracking-wide text-burgundy-900 sm:text-5xl">
              Misafir Sayınıza Uygun Bahçe
            </h2>
            <div className="mx-auto mt-6 h-px w-24 bg-gold-400/70" aria-hidden />
            <p className="mx-auto mt-8 max-w-3xl text-base font-light leading-relaxed text-yali-700">
              Davetinizin zarif bir ortamda geçmesi için her misafir aralığına minimum bahçe alanı şartı uyguluyoruz.
              Arama sonuçlarında yalnızca bu şartı sağlayan mülkleri görürsünüz.
            </p>
          </div>

          <div className="mt-14 grid gap-8 sm:grid-cols-3">
            {EVENT_TIERS.map((t) => (
              <div
                key={t.tier}
                className="bg-white p-8 text-center shadow-card transition-transform duration-500 hover:-translate-y-1"
                data-testid={`tier-card-${t.tier}`}
              >
                <span className="font-display text-xs uppercase tracking-[0.3em] text-gold-600">Tier {t.tier}</span>
                <h3 className="mt-4 font-display text-2xl font-light tracking-wide text-burgundy-900">{t.labelTr}</h3>
                <div className="mx-auto my-5 h-px w-12 bg-gold-300" aria-hidden />
                <p className="text-sm font-light leading-relaxed text-yali-700">
                  <strong className="font-medium text-burgundy-800">{t.minGuests}–{t.maxGuests} misafir</strong> için{' '}
                  <strong className="font-medium text-burgundy-700" data-testid={`tier-garden-${t.tier}`}>
                    en az {t.minGardenM2} m²
                  </strong>{' '}
                  bahçe
                </p>
                <p className="mt-3 text-xs font-light tracking-wide text-gold-700">
                  Önerilen: {t.recommendedGardenM2} m² ve üzeri
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ HOST CTA ============ */}
      <section className="relative isolate overflow-hidden">
        <img
          src={BANNERS.home}
          alt=""
          aria-hidden
          className="absolute inset-0 -z-10 h-full w-full object-cover"
        />
        <div className="absolute inset-0 -z-10 bg-burgundy-950/75" aria-hidden />

        <div className="yd-container flex flex-col items-center gap-6 py-24 text-center">
          <h2 className="font-display text-4xl font-light tracking-wide text-white sm:text-5xl">
            Yalınızı Davetlere Açın
          </h2>
          <div className="h-px w-24 bg-gold-400/70" aria-hidden />
          <p className="max-w-2xl text-base font-light leading-relaxed text-cream-100">
            Mülkünüzü listeleyin, organizasyon tiplerini seçin ve doğrulanmış misafirlerden rezervasyon alın.
          </p>
          <Link
            to="/ilanlarim/yeni"
            className="mt-4 inline-flex items-center gap-2 border-b border-gold-400 pb-1 font-display text-sm uppercase tracking-[0.25em] text-cream-100 transition-colors hover:border-white hover:text-white"
          >
            Yeni İlan Oluştur <ArrowRight className="h-4 w-4" aria-hidden />
          </Link>
        </div>
      </section>
    </>
  );
}
